import { appAxios } from "@/core/utils/appAxios";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import { useI18n } from "@/shared/composables/use-i18n";
import { i18n } from "@/shared/i18n/i18n";
import { watch } from "@vue/runtime-core";
import { useToast } from "primevue/usetoast";
import { SubmissionContext, useField, useForm } from "vee-validate";
import { Options, setup, Vue } from "vue-class-component";
import * as yup from "yup";

import {
    APIResponse,
    EnumCompanyStatus,
    EnumUserRole,
    GenderDto,
    LanguageDto,
    PatchUserRequestBody,
    PhoneNumberDto,
    PhoneNumberTypeDto,
    PostUserPhoneNumbersRequestBody,
    UserDto,
} from "@/shared/api-contract/api-contract";
import { sessionModel } from "@/shared/state/session.model";
import { currentUserModel } from "@/shared/state/current-user.model";
import { isNil, omit } from "lodash";
import { defaultPhoneProps } from "@/dashboard/my-account/contact-info/constants";
import { appModel } from "@/app.model";
import { currentCompanyModel } from "@/shared/state/current-company.model";
import Dropdown from "primevue/dropdown";
import AccountManager from "@/dashboard/my-account/account-manager/account-manager.vue";
import { getEnvValue } from "@/shared/models/environment";

const HIDE_CONTACT_INFO_BRANDS = ["astara"];

@Options({
    components: {
        "app-account-manager": AccountManager,
        Dropdown,
    },
})
export class ContactInfo extends Vue {
    toast = setup(useToast);
    i18n = setup(useI18n);

    userDto: UserDto | undefined = undefined;

    languageOptions = [] as readonly LanguageDto[];
    genderOptions = [] as readonly GenderDto[];
    phoneNumbers: PhoneNumberDto[] = [];
    phoneNumberType: PhoneNumberTypeDto[] = [];

    firstnameFocused = false;
    lastnameFocused = false;
    emailFocused = false;
    languageFocused = false;
    genderFocused = false;
    phoneNumberTypeFocused = false;
    idFocused = false;
    roleFocused = false;
    isLoading = false;
    phoneFocused = false;
    selectedOption = null;
    marketingSubscriptionFocused = false;
    marketingOnCompanyProfileFocused = false;

    phoneNumber = {
        number: "",
    };
    previousValidNumber = "";

    form = setup(() => {
        const form = useForm<PatchUserRequestBody>();

        useField("firstname", yup.string().required().max(255));
        useField("lastname", yup.string().required().max(255));
        useField("id_language", yup.number().required());
        useField("marketing_subscription", yup.boolean());
        useField("marketing_on_company_profile", yup.boolean());
        return form;
    });

    mounted() {
        this.fetchAvailableLanguages();
        this.fetchAvailableGenders();
        this.fetchUserPhoneNumbers();
        this.fetchPhoneNumberType();

        watch(
            () => ({
                lang: appLanguageSwitcherModel.lang.value,
                accessToken: sessionModel.sessionDto.value,
            }),
            async ({ accessToken, lang }) => {
                if (!accessToken) {
                    return;
                }

                this.fetchCurrentUser();
            },
            { immediate: true },
        );
    }

    phoneProps(number: string) {
        const item = this.phoneNumbers.find((item) => item.number === number);

        if (item?.number && defaultPhoneProps.validCountryCodes.includes(item?.number)) {
            const styleClasses = "form__input";

            return {
                ...defaultPhoneProps,
                inputOptions: {
                    ...defaultPhoneProps.inputOptions,
                    styleClasses,
                },
            };
        } else {
            let styleClasses =
                !isNil(number) && item?.phoneNumberIsValid
                    ? "form__input"
                    : ["form__input", "--error"];

            return {
                ...defaultPhoneProps,
                inputOptions: {
                    ...defaultPhoneProps.inputOptions,
                    styleClasses,
                },
            };
        }
    }

    validatePhone(phoneObject: { valid: boolean }, number: string) {
        const item = this.phoneNumbers.find((item) => item.number === number);
        if (item !== undefined) {
            item.phoneNumberIsValid = phoneObject.valid;
        }
    }

    validateInput(event) {
        const value = event.target.value;
        const cleanedValue = value.replace(/[\s+]/g, "");
        if (/^\d*$/.test(cleanedValue)) {
            this.previousValidNumber = value;
            this.phoneNumber.number = value;
        } else {
            event.target.value = this.previousValidNumber;
        }
    }

    handleFocus(number: string) {
        this.previousValidNumber = number;
    }
    async uploadPhone(phoneNumber: PhoneNumberDto) {
        if (phoneNumber.id === null) {
            const requestBody: PostUserPhoneNumbersRequestBody = {
                id_type: Number(phoneNumber.type),
                number: phoneNumber.number.length < 5 ? null : phoneNumber.number,
            };
            await this.createPhoneNumber(requestBody);
            await this.fetchUserPhoneNumbers();
        } else if (!phoneNumber.edit) {
            this.editPhoneNumber(phoneNumber.id, true);
        } else {
            this.editPhoneNumber(phoneNumber.id, false);
            this.updatePhoneNumbers(phoneNumber.id);
        }
    }

    onKeyDate(event) {
        event.target.value = event.target.value.replace(
            /^(\d{2})(\d{2})(\d{4})/,
            "$1/$2/$3",
        );
    }

    deletePhone(phoneNumber: PhoneNumberDto) {
        if (phoneNumber.id === null) {
            const indexToDelete = this.phoneNumbers.findIndex(
                (item) => item.number === phoneNumber.number,
            );

            if (indexToDelete !== -1) {
                this.phoneNumbers.splice(indexToDelete, 1);
            }
        } else {
            this.deletePhoneNumber(phoneNumber.id);
        }
    }

    async createPhoneNumber(requestBody: PostUserPhoneNumbersRequestBody) {
        await appAxios.value
            .post<APIResponse<PhoneNumberDto>>(
                "/client/web/user/phone-numbers",
                requestBody,
            )
            .then((response) => {
                if (response.status === 200 && response.data.status.code === 200) {
                    const phoneNumber: PhoneNumberDto = response.data.data;

                    this.phoneNumbers = this.phoneNumbers.map((item) => {
                        if (item.number === phoneNumber.number) {
                            return phoneNumber;
                        }
                        return item;
                    });

                    appModel.toast.value.add({
                        severity: "success",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.phoneNumber",
                        )} ${i18n.global.t("common.added")}`,
                        life: 3000,
                    });
                } else {
                    appModel.toast.value.add({
                        severity: "error",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.phoneNumber",
                        )} ${i18n.global.t("common.error")}`,
                        life: 3000,
                    });
                }
            })
            .catch((e) => console.log(e));
    }

    editPhoneNumber(id: number, edit: boolean) {
        this.phoneNumbers = this.phoneNumbers.map((phone) => {
            if (phone.id === id) {
                return { ...phone, edit };
            } else {
                return phone;
            }
        });
    }

    async updatePhoneNumbers(id: number) {
        const item = this.phoneNumbers.find((item) => item.id === id);
        await appAxios.value
            .patch(`/client/web/user/phone-numbers/${id}`, omit(item, "edit"))
            .then((res) => {
                if (res.data.status.code === 200) {
                    appModel.toast.value.add({
                        severity: "success",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.phoneNumber",
                        )} ${i18n.global.t("common.updated")}`,
                        life: 3000,
                    });
                } else {
                    appModel.toast.value.add({
                        severity: "error",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.phoneNumber",
                        )} ${i18n.global.t("common.error")}`,
                        life: 3000,
                    });
                }
            })
            .catch((e) => console.log(e));
    }

    async deletePhoneNumber(id: number) {
        await appAxios.value
            .delete(`/client/web/user/phone-numbers/${id}`)
            .then((res) => {
                if (res.data.status.code === 200) {
                    this.fetchUserPhoneNumbers();
                    appModel.toast.value.add({
                        severity: "success",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.phoneNumber",
                        )} ${i18n.global.t("common.deleted")}`,
                        life: 3000,
                    });
                } else {
                    appModel.toast.value.add({
                        severity: "error",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.phoneNumber",
                        )} ${i18n.global.t("common.error")}`,
                        life: 3000,
                    });
                }
            })
            .catch((e) => console.log(e));
    }

    get isViewer(): boolean {
        return sessionModel.isViewer;
    }

    get isReadOnlyMode(): boolean {
        switch (currentUserModel.userDto.value?.role.id) {
            case EnumUserRole.ADMINISTRATOR:
                return false;
            case EnumUserRole.PURCHASOR:
            case EnumUserRole.VIEWER:
                // case EnumUserRole.DEACTIVATED:
                return true;
        }

        return true;
    }

    get canUploadLogo() {
        return (
            currentCompanyModel.companyDto.value &&
            currentCompanyModel.companyDto.value?.status.id >= EnumCompanyStatus.APPROVED
        );
    }
    async fetchUserPhoneNumbers() {
        await appAxios.value
            .get<APIResponse<PhoneNumberDto[]>>("/client/web/user/phone-numbers")
            .then((response) => {
                if (response.status === 200 && response.data.status.code === 200) {
                    this.phoneNumbers = response.data.data.map((phone) => ({
                        ...phone,
                        edit: false,
                    }));
                }
            });
    }
    get onSubmit() {
        return this.form.handleSubmit(this.handleSubmit.bind(this));
    }

    async handleSubmit(
        values: PatchUserRequestBody,
        form: SubmissionContext<PatchUserRequestBody>,
    ) {
        this.isLoading = true;

        const date_of_birth = values.date_of_birth
            ? Math.floor(
                  (new Date(values.date_of_birth).setUTCHours(0, 0, 0, 0) -
                      defaultPhoneProps.epochStart) /
                      defaultPhoneProps.millisecondsPerDay,
              ) + 1
            : null;

        await appAxios.value
            .patch<APIResponse<UserDto>>("/client/web/user", { ...values, date_of_birth })
            .then((response) => {
                if (response.status === 200 && response.data.status.code === 200) {
                    this.userDto = response.data.data;
                    this.form.resetForm({
                        values: {
                            firstname: response.data.data.firstname,
                            lastname: response.data.data.lastname,
                            id_language: response.data.data.language.id,
                            id_gender: response.data.data.gender?.id,
                            date_of_birth: new Date(
                                response.data.data.date_of_birth *
                                    defaultPhoneProps.millisecondsPerDay +
                                    defaultPhoneProps.epochStart,
                            ),
                            marketing_subscription:
                                response.data.data.marketing_subscription,
                            marketing_on_company_profile:
                                response.data.data.marketing_on_company_profile,
                        },
                    });
                    appModel.toast.value.add({
                        severity: "success",
                        summary: `${i18n.global.t("dashboard.myAccount")} ${i18n.global.t(
                            "common.updated",
                        )}`,
                        life: 3000,
                    });
                } else {
                    appModel.toast.value.add({
                        severity: "error",
                        summary: `${i18n.global.t(
                            "dashboard.myAccountComponent.companyInfoTab.accountManager",
                        )} ${i18n.global.t("common.error")}`,
                        life: 3000,
                    });
                }
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    async fetchCurrentUser() {
        await appAxios.value
            .get<APIResponse<UserDto>>(`/client/web/user`)
            .then((response) => {
                if (response.status !== 200 || response.data.status.code !== 200) {
                    if (response.status === 401 || response.data.status.code === 401) {
                        this.userDto = undefined;
                    }
                } else {
                    this.userDto = response.data.data;

                    const userDateOfBirth = new Date(
                        defaultPhoneProps.epochStart +
                            response.data.data.date_of_birth *
                                defaultPhoneProps.millisecondsPerDay,
                    );
                    userDateOfBirth.setUTCHours(0, 0, 0, 0);

                    this.form.resetForm({
                        values: {
                            firstname: response.data.data.firstname,
                            lastname: response.data.data.lastname,
                            id_language: response.data.data.language.id,
                            id_gender: response.data.data.gender?.id,
                            date_of_birth: userDateOfBirth,
                            marketing_subscription:
                                response.data.data.marketing_subscription,
                            marketing_on_company_profile:
                                response.data.data.marketing_on_company_profile,
                        },
                    });
                }
            })
            .catch(console.log);
    }

    createPhoneNumberRow() {
        const apiRequestBody: PhoneNumberDto = {
            id: null,
            phoneNumberIsValid: false,
            number: "",
            type: null,
            edit: true,
        };

        this.phoneNumbers.unshift(apiRequestBody);
    }
    async fetchAvailableLanguages() {
        await appAxios.value
            .get<APIResponse<LanguageDto[]>>(`/client/web/enumerator/language`)
            .then((response) => {
                if (response.data.status.code === 200) {
                    this.languageOptions = response.data.data;
                }
            });
    }

    async fetchPhoneNumberType() {
        await appAxios.value
            .get<APIResponse<LanguageDto[]>>(
                `/client/web/enumerator/contact/phone-number-type`,
            )
            .then((response) => {
                if (response.data.status.code === 200) {
                    this.phoneNumberType = response.data.data;
                }
            });
    }

    async fetchAvailableGenders() {
        await appAxios.value
            .get<APIResponse<GenderDto[]>>(`/client/web/enumerator/contact/gender`)
            .then((response) => {
                if (response.data.status.code === 200) {
                    this.genderOptions = response.data.data;
                }
            });
    }

    get brandLabel() {
        return getEnvValue("BRAND_LABEL");
    }

    get shouldHideContactInfo() {
        return HIDE_CONTACT_INFO_BRANDS.includes(this.brandLabel);
    }
}
